
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type UserRole = "admin" | "enterprise" | "pro" | "basic";

// Return the user's most privileged role (admin > enterprise > pro > basic)
export const useUserRole = () => {
  const { user } = useAuth();
  
  // For testing purposes, you can uncomment this line to force admin role
  const FORCE_ADMIN = true; // Set to true to force admin role for any user
  
  const { data: userRole, isLoading } = useQuery({
    queryKey: ["user_role", user?.id],
    queryFn: async () => {
      if (!user?.id) return "basic";
      
      console.log("Fetching role for user:", user.id); // Add diagnostic log
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching user role:", error);
        throw error;
      }
      
      console.log("User roles data:", data); // Log the returned roles data
      
      // Choose highest role if multiple assigned
      const priorities = ["admin", "enterprise", "pro", "basic"];
      if (!data || data.length === 0) return "basic";
      return data
        .map((r: any) => r.role)
        .sort((a: string, b: string) => priorities.indexOf(a) - priorities.indexOf(b))[0];
    },
    enabled: !!user?.id,
  });

  // When FORCE_ADMIN is true, override the role to admin
  const effectiveRole = FORCE_ADMIN ? "admin" : userRole;
  
  // Calculate role flags based on the effective role
  const isAdmin = effectiveRole === "admin";
  const isEnterpriseUser = effectiveRole === "enterprise";
  const isProUser = effectiveRole === "pro";
  const isBasicUser = effectiveRole === "basic";

  console.log("useUserRole hook results:", { 
    userFromDB: userRole, 
    effectiveRole, 
    isAdmin, 
    forceAdminEnabled: FORCE_ADMIN 
  });

  return { 
    userRole: effectiveRole, 
    isLoading,
    isAdmin,
    isEnterpriseUser,
    isProUser,
    isBasicUser
  };
};
